'use client';

import { memo } from 'react';
import { VStack, Box, token } from '@terra/ui-styled-system';
import { Text, Tooltip } from '@terra/ui-design-system';
import { CrownIcon, Key01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  useReactFlow,
  type Node,
  Handle,
  Position,
  NodeToolbar,
  BaseEdge,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import {
  GRAPH_EDGE_COLOR,
  GRAPH_NODE_COLOR,
  GRAPH_NODE_ICON,
  type InternalNetworkNodeData,
} from './constants';
import { InternalNetworkGraphNodePopover } from './InternalNetworkGraphNodePopover';
import { useInternalNetworkGraphNodeActions } from './InternalNetworkGraphNodeActionsContext';

const SELECTED_NODE_RING_SHADOW = `0 0 0 6px ${token('colors.border.accent.focus')}`;
const CROWN_JEWEL_RING_SHADOW = `0 0 0 4px ${token('colors.orange.9')}`;
const OWNED_NODE_RING_SHADOW = `0 0 0 4px ${token('colors.bg.graphs.blue')}, 0 0 8px 2px ${token('colors.bg.graphs.blue')}`;
const MAX_LABEL_WIDTH = 120;
const LABEL_HEIGHT = 80;
const NODE_WIDTH = 180;
const NODE_HEIGHT_WITH_LABEL = 110;
const NODE_HEIGHT_COMPACT = 72;
const NODE_ICON_SIZE = 48;
const NODE_ICON_SIZE_COMPACT = 36;

type InternalNetworkNodeProps = {
  id: string;
  data: InternalNetworkNodeData;
  selected?: boolean;
};

export const InternalNetworkNode = memo(
  ({ id, data, selected }: InternalNetworkNodeProps) => {
    const t = useTranslations('internalNetwork');
    const nodeActions = useInternalNetworkGraphNodeActions();
    const Icon = GRAPH_NODE_ICON[data.entityKind] ?? GRAPH_NODE_ICON.USER;
    const color = GRAPH_NODE_COLOR[data.entityKind] ?? GRAPH_EDGE_COLOR;
    const entityKindLabel = t(
      `crownJewels.entityKinds.${data.entityKind}` as Parameters<typeof t>[0],
    );
    const showNodeLabel = data.showNodeLabel !== false;
    const isCompactDensity = data.density === 'compact';
    const iconSize = isCompactDensity ? NODE_ICON_SIZE_COMPACT : NODE_ICON_SIZE;
    const iconInnerSize = isCompactDensity ? 14 : 18;
    const ringShadows = [
      selected || data.highlighted ? SELECTED_NODE_RING_SHADOW : null,
      data.isCrownJewel ? CROWN_JEWEL_RING_SHADOW : null,
      data.isOwned ? OWNED_NODE_RING_SHADOW : null,
    ].filter(Boolean);
    const showToolbar =
      selected === true &&
      nodeActions?.selectedNodeId === id &&
      nodeActions.selectedNodeId !== null;

    return (
      <Box
        position="relative"
        opacity={data.hidden ? 0.18 : data.dimmed ? 0.35 : 1}
        transition="opacity 0.15s ease"
      >
        {showToolbar && nodeActions ? (
          <NodeToolbar
            isVisible
            position={Position.Right}
            align="start"
            offset={16}
          >
            <InternalNetworkGraphNodePopover
              node={{
                id,
                kind: data.entityKind,
                label: data.label,
                objectId: data.objectId,
                samAccountName: data.samAccountName ?? null,
                distinguishedName: data.distinguishedName ?? null,
                isCrownJewel: data.isCrownJewel,
                isOwned: data.isOwned,
              }}
              isExpanding={nodeActions.isExpanding === true}
              onExpand={() => {
                nodeActions.onCloseSelection();
                nodeActions.onExpand(id);
              }}
              onViewDetails={() => nodeActions.onViewDetails(id)}
              onClose={nodeActions.onCloseSelection}
            />
          </NodeToolbar>
        ) : null}
        <VStack
          gap={0}
          alignItems="center"
          transition="transform 0.15s ease"
          _hover={{ transform: data.dimmed ? undefined : 'scale(1.05)' }}
          cursor="pointer"
        >
          <Box position="relative">
            {isCompactDensity ? (
              <>
                <Handle
                  type="target"
                  position={Position.Left}
                  style={{ opacity: 0 }}
                />
                <Handle
                  type="source"
                  position={Position.Right}
                  style={{ opacity: 0 }}
                />
              </>
            ) : null}
            <Box
              w={`${iconSize}px`}
              h={`${iconSize}px`}
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: color,
                ...(ringShadows.length > 0
                  ? { boxShadow: ringShadows.join(', ') }
                  : {}),
              }}
              mb={showNodeLabel ? (isCompactDensity ? 2 : 3) : 0}
            >
              <Icon size={iconInnerSize} color="white" strokeWidth={2} />
            </Box>
            {data.isCrownJewel ? (
              <Box
                position="absolute"
                top="-4px"
                right="-4px"
                w="18px"
                h="18px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                backgroundColor="orange.9"
                borderWidth="1px"
                borderColor="bg.default"
              >
                <CrownIcon size={10} color="white" strokeWidth={2.5} />
              </Box>
            ) : null}
            {data.isOwned ? (
              <Box
                position="absolute"
                top="-6px"
                left="-6px"
                w="22px"
                h="22px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                backgroundColor="blue.9"
                borderWidth="2px"
                borderColor="bg.default"
                style={{
                  boxShadow: `0 0 6px 1px ${token('colors.bg.graphs.blue')}`,
                }}
              >
                <Key01Icon size={12} color="white" strokeWidth={2.5} />
              </Box>
            ) : null}
          </Box>

          {showNodeLabel ? (
            <>
              <Box mb={1.5} position="relative">
                {isCompactDensity ? null : (
                  <Handle
                    type="target"
                    position={Position.Left}
                    style={{ opacity: 0 }}
                  />
                )}
                <Tooltip title={data.label}>
                  <Text
                    fontSize={isCompactDensity ? 'xs' : 'sm'}
                    fontWeight={500}
                    color="fg.default"
                    maxW={isCompactDensity ? '120px' : '180px'}
                    truncate
                    textAlign="center"
                  >
                    {data.label}
                  </Text>
                </Tooltip>
                {isCompactDensity ? null : (
                  <Handle
                    type="source"
                    position={Position.Right}
                    style={{ opacity: 0 }}
                  />
                )}
              </Box>

              {!isCompactDensity ? (
                <Box mb={1.5}>
                  <Text fontSize="sm" color="fg.muted">
                    {entityKindLabel}
                  </Text>
                </Box>
              ) : null}

              {!isCompactDensity && data.samAccountName ? (
                <Text fontSize="xs" color="fg.subtle" maxW="180px" truncate>
                  {data.samAccountName}
                </Text>
              ) : null}
            </>
          ) : (
            <>
              <Handle
                type="target"
                position={Position.Left}
                style={{ opacity: 0 }}
              />
              <Handle
                type="source"
                position={Position.Right}
                style={{ opacity: 0 }}
              />
              <Tooltip title={`${data.label} · ${entityKindLabel}`}>
                <Text fontSize="xs" color="fg.muted" maxW="72px" truncate>
                  {data.label}
                </Text>
              </Tooltip>
            </>
          )}
        </VStack>
      </Box>
    );
  },
);
InternalNetworkNode.displayName = 'InternalNetworkNode';

function getNodeBounds(node: Node<InternalNetworkNodeData>) {
  const width = node.measured?.width ?? node.width ?? NODE_WIDTH;
  const height =
    node.measured?.height ??
    node.height ??
    (node.data.showNodeLabel === false
      ? NODE_HEIGHT_COMPACT
      : NODE_HEIGHT_WITH_LABEL);

  return {
    centerX: node.position.x + width / 2,
    centerY: node.position.y + height / 2,
    width,
    height,
  };
}

function getNearestNodeSide({
  fromX,
  fromY,
  toX,
  toY,
}: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}) {
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX > 0 ? Position.Right : Position.Left;
  }

  return deltaY > 0 ? Position.Bottom : Position.Top;
}

function getNodeConnectionPoint({
  centerX,
  centerY,
  width,
  height,
  side,
}: {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  side: Position;
}) {
  if (side === Position.Left) {
    return { x: centerX - width / 2, y: centerY };
  }

  if (side === Position.Right) {
    return { x: centerX + width / 2, y: centerY };
  }

  if (side === Position.Top) {
    return { x: centerX, y: centerY - height / 2 };
  }

  return { x: centerX, y: centerY + height / 2 };
}

function getEdgeCoordinates({
  sourceNode,
  targetNode,
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceNode: Node<InternalNetworkNodeData> | undefined;
  targetNode: Node<InternalNetworkNodeData> | undefined;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}) {
  if (!sourceNode || !targetNode) {
    return {
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  }

  const sourceBounds = getNodeBounds(sourceNode);
  const targetBounds = getNodeBounds(targetNode);
  const sourcePosition = getNearestNodeSide({
    fromX: sourceBounds.centerX,
    fromY: sourceBounds.centerY,
    toX: targetBounds.centerX,
    toY: targetBounds.centerY,
  });
  const targetPosition = getNearestNodeSide({
    fromX: targetBounds.centerX,
    fromY: targetBounds.centerY,
    toX: sourceBounds.centerX,
    toY: sourceBounds.centerY,
  });
  const sourcePoint = getNodeConnectionPoint({
    ...sourceBounds,
    side: sourcePosition,
  });
  const targetPoint = getNodeConnectionPoint({
    ...targetBounds,
    side: targetPosition,
  });

  return {
    sourceX: sourcePoint.x,
    sourceY: sourcePoint.y,
    targetX: targetPoint.x,
    targetY: targetPoint.y,
    sourcePosition,
    targetPosition,
  };
}

function getSelfLoopPath(node: Node<InternalNetworkNodeData>) {
  const bounds = getNodeBounds(node);
  const loopRadius = NODE_ICON_SIZE * 0.72;
  const centerX = bounds.centerX;
  const anchorY = bounds.centerY - bounds.height / 2 + NODE_ICON_SIZE / 2;

  return [
    `M ${centerX - loopRadius * 0.55} ${anchorY}`,
    `C ${centerX - loopRadius * 1.35} ${anchorY - loopRadius * 1.15}`,
    `${centerX + loopRadius * 1.35} ${anchorY - loopRadius * 1.15}`,
    `${centerX + loopRadius * 0.55} ${anchorY}`,
  ].join(' ');
}

const resolveGraphNodeColor = (
  entityKind: InternalNetworkNodeData['entityKind'] | undefined,
) =>
  (entityKind ? GRAPH_NODE_COLOR[entityKind] : undefined) ?? GRAPH_EDGE_COLOR;
const InternalNetworkEdgeComponent = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  source,
  target,
  style,
  label,
  data,
  markerEnd,
  selected,
}: EdgeProps) => {
  const { getNode } = useReactFlow<Node<InternalNetworkNodeData>>();
  const sourceNode = getNode(source);
  const targetNode = getNode(target);
  const edgeData = (data ?? {}) as {
    simplified?: boolean;
    showEdgeLabels?: boolean;
    compactLabel?: boolean;
    dimmed?: boolean;
    isAbusable?: boolean;
    groupPosition?: number;
    groupSize?: number;
    relationshipType?: string;
  };
  const isSelfLoop = source === target;
  const sourceColor = resolveGraphNodeColor(sourceNode?.data?.entityKind);
  const targetColor = resolveGraphNodeColor(targetNode?.data?.entityKind);
  const strokeWidth = edgeData.isAbusable ? 2.5 : 2;
  let edgePath: string;
  let labelX: number;
  let labelY: number;
  let gradientStartX = sourceX;
  let gradientStartY = sourceY;
  let gradientEndX = targetX;
  let gradientEndY = targetY;

  if (isSelfLoop && sourceNode) {
    edgePath = getSelfLoopPath(sourceNode);
    const bounds = getNodeBounds(sourceNode);

    labelX = bounds.centerX;
    labelY = bounds.centerY - bounds.height / 2 - 12;
    gradientStartX = bounds.centerX;
    gradientStartY = bounds.centerY + bounds.height / 2;
    gradientEndX = bounds.centerX;
    gradientEndY = bounds.centerY - bounds.height / 2;
  } else {
    const edgeCoordinates = getEdgeCoordinates({
      sourceNode,
      targetNode,
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
    const groupSize = edgeData.groupSize ?? 1;
    const groupPosition = edgeData.groupPosition ?? 0;
    const curvature =
      groupSize > 1
        ? 0.2 + groupPosition * 0.18 - (groupSize - 1) * 0.09
        : 0.25;

    [edgePath] = getBezierPath({
      sourceX: edgeCoordinates.sourceX,
      sourceY: edgeCoordinates.sourceY,
      sourcePosition: edgeCoordinates.sourcePosition,
      targetX: edgeCoordinates.targetX,
      targetY: edgeCoordinates.targetY,
      targetPosition: edgeCoordinates.targetPosition,
      curvature,
    });
    labelX = (edgeCoordinates.sourceX + edgeCoordinates.targetX) / 2;
    labelY = (edgeCoordinates.sourceY + edgeCoordinates.targetY) / 2;
    gradientStartX = edgeCoordinates.sourceX;
    gradientStartY = edgeCoordinates.sourceY;
    gradientEndX = edgeCoordinates.targetX;
    gradientEndY = edgeCoordinates.targetY;
  }

  const labelText = label ? String(label) : null;
  const edgeOpacity = edgeData.dimmed ? 0.25 : (style?.opacity ?? 1);
  const gradientId = `internal-network-edge-gradient-${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  const usesGradient = !selected && !isSelfLoop && sourceColor !== targetColor;
  const edgeStroke = selected
    ? token('colors.border.accent.focus')
    : usesGradient
      ? `url(#${gradientId})`
      : sourceColor;

  return (
    <>
      {usesGradient ? (
        <defs>
          <linearGradient
            id={gradientId}
            x1={gradientStartX}
            y1={gradientStartY}
            x2={gradientEndX}
            y2={gradientEndY}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={sourceColor} />
            <stop offset="100%" stopColor={targetColor} />
          </linearGradient>
        </defs>
      ) : null}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth: selected ? strokeWidth + 1 : strokeWidth,
          stroke: edgeStroke,
          opacity: edgeOpacity,
        }}
        markerEnd={markerEnd}
        interactionWidth={20}
      />
      {labelText &&
        edgeData.showEdgeLabels !== false &&
        !edgeData.simplified && (
          <g>
            <foreignObject
              x={labelX - MAX_LABEL_WIDTH / 2}
              y={labelY - (edgeData.compactLabel ? 18 : LABEL_HEIGHT / 2)}
              width={MAX_LABEL_WIDTH}
              height={edgeData.compactLabel ? 36 : LABEL_HEIGHT}
              style={{
                opacity: edgeOpacity,
                pointerEvents: 'none',
              }}
            >
              <Box
                h="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  padding="0 8px"
                  borderRadius="sm"
                  backgroundColor="bg.neutral.hoverWhite"
                  fontSize="11px"
                  color="fg.muted"
                  fontWeight={400}
                  textAlign="center"
                  wordWrap="break-word"
                  overflowWrap="break-word"
                  maxWidth={`${MAX_LABEL_WIDTH}px`}
                  lineHeight="1.3"
                  whiteSpace="normal"
                  boxSizing="border-box"
                  style={{
                    display: '-webkit-box',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {labelText}
                </Box>
              </Box>
            </foreignObject>
          </g>
        )}
    </>
  );
};

export const InternalNetworkEdge = memo(InternalNetworkEdgeComponent);
InternalNetworkEdge.displayName = 'InternalNetworkEdge';

export const internalNetworkNodeTypes = {
  internalNetworkNode: InternalNetworkNode,
};
export const internalNetworkEdgeTypes = {
  internalNetworkEdge: InternalNetworkEdge,
};
export {
  buildInternalNetworkFlowElements,
  filterGraphToDirectNeighborhood,
  getInternalNetworkHighlightNodeIds,
  type InternalNetworkGraphViewScope,
} from './internal-network-graph.helpers';
